import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BaseButtonComponent } from '@d-components/button/button.component';
import { CancelButtonComponent } from '@d-components/cancel-button/cancel-button.component';
import { FilesUploaderComponent } from '@d-components/files-uploader/files-uploader.component';
import { ModalSize, ToastType } from '@d-constants';
import { BaseAttachmentModel, BaseResponseModel } from '@d-models';
import { TemplateUrl } from '@d-models/services/product-name.model';
import { ModalService, ToastService } from '@d-services';
import { XlsxService } from '@d-services/other/xlsx.service';
import { TranslateModule } from '@ngx-translate/core';
import {
  catchError,
  delay,
  forkJoin,
  Observable,
  take,
  throwError,
} from 'rxjs';
import { ConfirmPopupComponent } from '../confirm-popup/confirm-popup.component';
import { OptionsModel } from '@d-components/form/form.interface';
import { FormControl } from '@angular/forms';
import { BaseDropdownComponent } from '@d-components/dropdown/dropdown.component';
import { BulkWorkOrderRequestStatusOptions } from '@d-modules/work-order/work-order.constant';

@Component({
  selector: 'app-import-popup',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FilesUploaderComponent,
    BaseButtonComponent,
    CancelButtonComponent,
    BaseDropdownComponent,
  ],
  templateUrl: './import-popup.component.html',
  styleUrls: ['./import-popup.component.scss'],
})
export class ImportPopupComponent implements OnInit {
  mode: IMPORT_STEP = IMPORT_STEP.UPLOAD;
  @Input() importFunc!: (...args: any) => Observable<any>;
  @Input() submitFunc!: (...args: any) => any;
  // @Input() submitFunc!: (...args: any) => Observable<any>;
  @Input() getTemplateFunc!: (
    ...args: any
  ) => Observable<BaseResponseModel<TemplateUrl>>;
  @Input() templateNameFunc!: (...args: any) => string;
  @Input() templateName: string = '';
  @Input() FILE_ATTACHMENT_IMPORT_TEXT = 'FILE_ATTACHMENT_IMPORT';
  @Input() acceptType = '.xlsx';
  @Input() SELECTION_TEXT? = 'Type';
  @Input() isOptionSelectable?: false;
  @Input() selectionOptions?: OptionsModel[];
  @Input() selectedValue?: OptionsModel;
  @Input() buttonText: {
    proceedBtn?: string;
    cancelBtn?: string;
    submitBtn?: string;
    exportErrorBtn?: string;
  };
  @Input() dataArea = {
    startRow: 1,
    startCol: 1,
    newFileName: '',
    sheetIndex: 0,
    autoRowNumber: false,
  };
  @Input() submitSuccessMessage: string =
    'FILENAME_HAS_BEEN_SUCCESSFULLY_IMPORTED';
  @Input() overrideFlag: boolean = true;
  @Input() downloadMessage: string = '';
  @Input() shouldAddErrorSuffixToFileName: boolean = false;

  templateImportUrl: string = '';
  historyImportId: string = '';
  selectedAttachments: BaseAttachmentModel[] = [];
  totalFailRecords!: number;
  totalRecords!: number;
  title!: string;
  currentFileData: any;
  submitData: any;
  disableSubmit: boolean = false;
  responseErrorData: {
    rowNumber: number;
    errorMessage: string;
    overrideFlagText: string;
    [key: string]: any;
  }[] = [];

  constructor(
    private modalService: ModalService,
    private xlsxService: XlsxService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    if (this.getTemplateFunc)
      this.getTemplateFunc(this.selectedValue).subscribe((val) => {
        this.templateImportUrl = val.data.url;
      });

    if (this.templateNameFunc) {
      this.templateName = this.templateNameFunc(this.selectedValue);
    }
  }

  getSelectedValueFormControl() {
    let formControl = new FormControl('selectedValue');

    formControl.setValue(this.selectedValue.value);

    return formControl;
  }

  onFileSelected(file: any) {
    this.selectedAttachments = file;
  }

  onOptionSelected(selectedItem: any) {
    this.selectedValue = this.getSelectionItemByValue(selectedItem);
    if (this.getTemplateFunc) {
      this.getTemplateFunc(this.selectedValue)
        .pipe(take(1))
        .subscribe((val) => {
          this.templateImportUrl = val.data.url;
        });
    }

    if (this.templateNameFunc) {
      this.templateName = this.templateNameFunc(this.selectedValue);
    }
  }

  private getSelectionItemByValue(value: any): OptionsModel {
    return this.selectionOptions?.find((option) => option.value === value);
  }

  processData(event: any) {
    this.mode = IMPORT_STEP.PROCESS;

    const fileData$ = this.xlsxService.readSheet(
      this.selectedAttachments[0],
      this.dataArea.sheetIndex,
      {
        startRow: this.dataArea.startRow,
      },
    );

    const processFile$ = this.importFunc(
      this.selectedAttachments[0],
      this.selectedValue,
    ).pipe(delay(2000));

    forkJoin({ fileData$, processFile$ })
      .pipe(
        catchError((e: any) => {
          this.mode = IMPORT_STEP.UPLOAD;
          this.selectedAttachments = [];
          return throwError(() => e);
        }),
      )
      .subscribe({
        next: ({ fileData$: fileData, processFile$: processFile }) => {
          this.currentFileData = fileData;
          this.title = this.selectedAttachments[0].name;
          this.totalRecords = processFile.data.totalRecord;
          if (processFile.data.totalError) {
            this.mode = IMPORT_STEP.WARNING;
            this.totalFailRecords = processFile.data.totalError;
            this.responseErrorData = processFile.data.imports;
          } else {
            this.mode = IMPORT_STEP.SUBMIT;
            this.historyImportId = processFile.data.historyImportId;
            this.submitData = processFile.data;
          }
        },
      });
  }

  exportErrorFile() {
    const additionalColumn = ['Error Message'];
    let debug = this.responseErrorData[1];
    if (this.overrideFlag) {
      additionalColumn.concat('Override flag');
    }
    const newHeader = [
      ...new Set(this.currentFileData.header.concat(additionalColumn)),
    ] as string[];

    const getErrorDataItem = (index: number) => {
      if (this.dataArea.autoRowNumber) return this.responseErrorData[index];

      return this.responseErrorData.find((i) => i.rowNumber === index + 1);
    };

    const newData = this.currentFileData.data.map(
      (data: any, index: number) => {
        const newItem = {} as any;
        newHeader.forEach((header: string) => {
          newItem[header] = data[header];

          if (header === 'Override flag') {
            newItem[header] = getErrorDataItem(index)?.overrideFlagText || '';
          }
          if (header === 'Error Message') {
            newItem[header] = getErrorDataItem(index)?.errorMessage || '';
          }
        });

        return newItem;
      },
    );

    this.xlsxService.overwriteWithData(this.selectedAttachments[0], newData, {
      startCol: this.dataArea.startCol,
      startRow: this.dataArea.startRow,
      sheetIndex: this.dataArea.sheetIndex,
      newFileName: this.shouldAddErrorSuffixToFileName
        ? this.createErrorFileName(this.selectedAttachments[0].name)
        : this.dataArea.newFileName || this.selectedAttachments[0].name,
    });

    this.mode = IMPORT_STEP.UPLOAD;
    this.selectedAttachments = [];
  }

  private createErrorFileName(filename: string): string {
    const extensionIndex = filename.lastIndexOf('.');
    if (extensionIndex === -1) {
      return filename + '-Error';
    }
    return (
      filename.slice(0, extensionIndex) +
      '-Error' +
      filename.slice(extensionIndex)
    );
  }

  handleUploadError(event: any) {
    if (event.type === 'INVALID_FILE_SIZE') {
      this.toastService.addSingle({
        type: ToastType.ERROR,
        message: 'ERROR_UPLOAD_OVER_5MB_SIZE',
        messageParams: {
          size: event.value,
        },
      });
    }
  }

  submit() {
    this.disableSubmit = true;

    this.submitFunc(this.historyImportId, this.submitData).subscribe((res) => {
      console.log('Log');
      this.toastService.addSingle({
        type: ToastType.SUCCESS,
        message: this.submitSuccessMessage,
        messageParams: {
          filename: this.title,
          type: this.selectedValue ? this.selectedValue.label : '',
        },
      });
      this.modalService.closeModal({ actionType: 'submit', data: res });
      this.disableSubmit = false;
    });
  }

  onCancel() {
    this.modalService.closeModal();
  }

  clickClose() {
    if (this.selectedAttachments.length)
      this.modalService
        .openModal(ConfirmPopupComponent, {
          title: 'CONFIRM_LEAVE_TITLE',
          confirmMessage: 'CONFIRM_LEAVE_BODY',
          showReason: false,
          cancelText: 'STAY',
          submitText: 'LEAVE',
          size: ModalSize.MEDIUM,
        })
        .subscribe((val) => {
          if (val.actionType === 'submit') {
            this.modalService.closeModal();
          }
        });
    else this.modalService.closeModal();
  }
}

enum IMPORT_STEP {
  UPLOAD = 'upload',
  PROCESS = 'process',
  SUBMIT = 'submit',
  WARNING = 'warning',
}
